/**
 * Same-page anchor scrolling (smooth).
 *
 * Horizon sets `overflow: hidden` on html/body and scrolls a `.page-wrapper`
 * container instead. Browsers only auto-scroll the document scrolling element
 * for fragment navigation, so a plain `href="#quote"` sets the hash and then
 * does nothing. We take over and scroll the container ourselves.
 *
 * Native `scroller.scrollTo({ behavior: 'smooth' })` animates correctly on this
 * container, whereas `scrollIntoView({ behavior: 'smooth' })` is silently
 * cancelled by the theme — so we compute the target offset and drive the
 * container directly.
 */
const SCROLLER = '.page-wrapper';

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Height of any sticky/fixed header pinned to the top, so the target doesn't
// land underneath it. Falls back to a sensible default.
function topInset() {
  let inset = 0;
  for (const el of document.querySelectorAll('.header-section, header, [class*="header"]')) {
    const cs = getComputedStyle(el);
    if (cs.position === 'sticky' || cs.position === 'fixed') {
      const rect = el.getBoundingClientRect();
      if (rect.top <= 1 && rect.height) inset = Math.max(inset, rect.bottom);
    }
  }
  return inset || 90;
}

function scrollToHash(hash, smooth = true) {
  if (!hash || hash === '#' || hash.length < 2) return false;

  let target;
  try {
    target = document.querySelector(hash);
  } catch {
    return false; // Not a valid selector (e.g. "#123").
  }
  if (!target) return false;

  // Only take over when the document itself is not the scroller.
  const scroller = document.querySelector(SCROLLER);
  if (!scroller || scroller.scrollHeight - scroller.clientHeight < 50) return false;

  const top = scroller.scrollTop + target.getBoundingClientRect().top - topInset();
  scroller.scrollTo({
    top: Math.max(0, top),
    behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'instant',
  });
  return true;
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href]');
  if (!link || link.target === '_blank' || event.defaultPrevented) return;

  const href = link.getAttribute('href');
  if (!href || !href.startsWith('#')) return;

  if (scrollToHash(href)) {
    event.preventDefault();
    // Keep the URL in sync so the link is still shareable and back works.
    history.pushState(null, '', href);
  }
});

// A hash typed straight into the address bar, or arrived at via back/forward.
window.addEventListener('hashchange', () => scrollToHash(location.hash));

if (location.hash) {
  // On first load, jump straight there — no animation down from the top.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => scrollToHash(location.hash, false));
  });
}
